/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";

import { GridColDef } from "@mui/x-data-grid-pro";
import { initLanContent } from "helpers";
import { useFetchingPartial } from "hooks";
import useFetchingImport from "hooks/useFetchingImport";
import { departmentListShortService, titleListShortService } from "services";
import {
  getJSONTemplateMasterData,
  renderTableExcelJSON,
  writeFileExcel,
  writeFileMultiTemplate,
} from "utils/xlsx";
import { utils, read } from "xlsx-js-style";

import { ModuleType } from "./types";
import {
  getConfigs,
  generateExcelTemplate,
  getBodyExcel,
  getService,
  getDataFromExcel,
  getFieldKey,
} from "./utils";

interface UseImportExcel {
  type: ModuleType;
  orgId: number;
  fileName?: string;
}

type RecordItem = Record<string, string | number | boolean | string[] | null>;

const useImportExcel = (props: UseImportExcel) => {
  const fetchAllDepartments = useFetchingImport({
    filters: {},
    serviceFn: departmentListShortService as any,
    totalEachRequest: 100,
  });
  const fetchAllTitle = useFetchingImport({
    filters: {},
    serviceFn: titleListShortService as any,
    totalEachRequest: 100,
  });
  const { type } = props;
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [rows, setRows] = useState<RecordItem[]>([]);
  const configs = useMemo(() => getConfigs(type), [type]);
  const importState = useFetchingPartial({
    serviceFn: getService(type),
    fieldKey: getFieldKey(type),
    totalEachRequest: 50,
    extendInfo: {
      organizationId: props.orgId,
    },
  });
  const [errorRows, setErrorRows] = useState<RecordItem[]>([]);

  const enableImport = useMemo(() => {
    return (
      rows?.length &&
      rows?.every(
        (item) => Array.isArray(item?.invalidList) && !item?.invalidList?.length
      )
    );
  }, [rows]);

  const readExcel = (_file: File | null) => {
    if (_file) {
      setRows([]);
      const reader = new FileReader();

      reader.onload = (e) => {
        setLoading(true);

        const binaryString = e.target?.result as string;
        const workbook = read(binaryString, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const newData = utils.sheet_to_json(sheet, { header: 1 });

        setRows(getDataFromExcel(newData, configs));
        setLoading(false);
        setModal(true);
      };

      reader.readAsBinaryString(_file);
    }
  };

  const columns: GridColDef<RecordItem>[] = useMemo(() => {
    return configs.map((item) => ({
      field: item.field,
      headerName: item.text,
      width: item.width || 200,
    }));
  }, [configs]);

  const handleSubmitImport = async () => {
    const result = await importState.handleService(rows);
    setErrorRows(result);
  };

  const handleGetTemplate = async () => {
    if (type === "employee") {
      const dataJSON = renderTableExcelJSON({
        data: generateExcelTemplate(configs),
      });
      const departments = await fetchAllDepartments.fetchAllData();
      const titles = await fetchAllTitle.fetchAllData();
      writeFileMultiTemplate({
        fileName: props?.fileName || "template",
        dataByTab: [
          {
            name: initLanContent(["Nhân viên", "Employee"]),
            data: dataJSON,
          },
          {
            name: initLanContent(["Phòng ban", "Department"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Phòng ban", "Department"]),
              departments.map((item) => ({
                code: item.code,
                nativeName: item.name,
              }))
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Chức vụ", "Title"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Chức vụ", "Title"]),
              titles.map((item) => ({
                code: item.code,
                nativeName: item.name,
              }))
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Giới tính", "Gender"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Giới tính", "Gender"]),
              [
                { code: "Male", nativeName: "Nam" },
                { code: "Female", nativeName: "Nữ" },
                { code: "Other", nativeName: "Khác" },
              ]
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Trạng thái hôn nhân", "Material Status"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Trạng thái hôn nhân", "Material Status"]),
              [
                { code: "Single", nativeName: "Độc thân" },
                { code: "Married", nativeName: "Đã kết hôn" },
                { code: "Divorced", nativeName: "Ly dị" },
                { code: "Widowed", nativeName: "Góa chồng" },
                { code: "Other", nativeName: "Khác" },
              ]
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Loại nhân viên", "Employment type"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Loại nhân viên", "Employment type"]),
              [
                { code: "FTE", nativeName: "NV toàn thời gian" },
                { code: "PTE", nativeName: "NV bán thời gian" },
                { code: "ITE", nativeName: "Nhân viên thực tập" },
                { code: "APE", nativeName: "Nhân viên học việc" },
                { code: "TEM", nativeName: "Nhân viên thời vụ" },
                { code: "CON", nativeName: "Nhân viên hợp đồng" },
              ]
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Trạng thái làm việc", "Employment status"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Loại nhân viên", "Employment type"]),
              [
                { code: "Probationary", nativeName: "Thử việc" },
                { code: "Termination", nativeName: "Đã nghỉ việc" },
                { code: "Working", nativeName: "Đang làm việc" },
                { code: "Maternity", nativeName: "Thai sản" },
                { code: "LongTermLeave", nativeName: "Nghỉ dài hạn" },
                { code: "LongTermSL", nativeName: "Nghỉ ốm dài hạn" },
                {
                  code: "LongTermUL",
                  nativeName: "Nghỉ dài hạn không hưởng lương",
                },
              ]
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
          {
            name: initLanContent(["Loại đối tượng", "Tax object type"]),
            data: getJSONTemplateMasterData(
              initLanContent(["Loại đối tượng", "Tax object type"]),
              [
                { code: "Resident", nativeName: "Resident" },
                { code: "NonResident", nativeName: "NonResident" },
              ]
            ),
            widths: [{ width: 30 }, { width: 30 }],
          },
        ],
      });
    } else {
      writeFileExcel({
        data: generateExcelTemplate(configs),
        fileName: "template",
      });
    }
  };

  const handleGetErrorFile = () => {
    writeFileExcel({
      data: getBodyExcel(errorRows, configs),
      fileName: "error",
    });
  };

  const handleReset = () => {
    setRows([]);
    setErrorRows([]);
  };

  return {
    modal,
    columns,
    rows,
    errorRows,
    loading,
    importState,
    enableImport,
    readExcel,
    setModal,
    handleSubmitImport,
    handleGetTemplate,
    handleGetErrorFile,
    handleReset,
  };
};

export default useImportExcel;
